import { PROPOSAL_COPY_FONT, formatProposalMoneySafe } from "@/lib/proposalTheme";
import {
  formatCustomerPricingDiscountLabel,
  type ProposalCustomerPricingDisplay,
} from "@shared/proposalCustomerPricing";
import { cn } from "@/lib/utils";

const money = { format: formatProposalMoneySafe };

type Props = {
  proposalTotal: number;
  finalAgreedPrice: number;
  showFinalAgreedPrice: boolean;
  discountAmount?: number;
  discountPercentage?: number;
  showDiscount?: boolean;
  showDiscountPercentage?: boolean;
  gstAmount?: number;
  showGst?: boolean;
  finalAmountPayable?: number;
  showFinalAmountPayable?: boolean;
  /** Hero layout for snapshot cards — emphasises agreed / payable over list total. */
  variant?: "default" | "hero";
};

export default function ProposalCustomerPricingPanel({
  proposalTotal,
  finalAgreedPrice,
  showFinalAgreedPrice,
  discountAmount,
  discountPercentage,
  showDiscount,
  showDiscountPercentage = false,
  gstAmount = 0,
  showGst = false,
  finalAmountPayable = finalAgreedPrice,
  showFinalAmountPayable = showFinalAgreedPrice || showGst,
  variant = "default",
}: Props) {
  const negotiated = showFinalAgreedPrice;
  const derivedDiscountAmount = Math.max(0, proposalTotal - finalAgreedPrice);
  const resolvedDiscountAmount = Number.isFinite(discountAmount)
    ? Math.max(0, Number(discountAmount))
    : derivedDiscountAmount;
  const resolvedDiscountPercentage = Number.isFinite(discountPercentage)
    ? Math.max(0, Number(discountPercentage))
    : proposalTotal > 0
      ? (resolvedDiscountAmount / proposalTotal) * 100
      : 0;
  const displayDiscount = negotiated && (showDiscount ?? resolvedDiscountAmount > 0.004);
  const discountLabel = formatCustomerPricingDiscountLabel({
    showDiscountPercentage: displayDiscount && showDiscountPercentage,
    discountPercentage: resolvedDiscountPercentage,
  });
  const primaryIsPayable = negotiated && showFinalAmountPayable && showGst;
  const primaryIsAgreed = negotiated && !primaryIsPayable;

  const proposalTotalClass = negotiated
    ? "text-sm font-medium tabular-nums text-[#1A2744]/65"
    : variant === "hero"
      ? "text-3xl font-semibold tracking-tight tabular-nums text-[#1A2744] sm:text-4xl"
      : "font-medium tabular-nums text-[#1A2744]";

  const proposalLabelClass = negotiated
    ? "text-[11px] font-medium uppercase tracking-[0.12em] text-[#1A2744]/50"
    : "text-xs font-semibold uppercase tracking-[0.12em] text-[#1A2744]/70";

  const agreedHeroClass =
    variant === "hero"
      ? "text-3xl font-semibold tracking-tight tabular-nums text-[#1A2744] sm:text-4xl"
      : "text-2xl font-semibold tabular-nums text-[#1A2744] sm:text-3xl";

  const payableHeroClass =
    variant === "hero"
      ? "text-3xl font-semibold tracking-tight tabular-nums text-[#1A2744] sm:text-4xl"
      : "text-2xl font-semibold tabular-nums text-[#1A2744] sm:text-3xl";

  return (
    <div
      className={cn(
        "rounded-xl border border-[#1A2744]/10 bg-white/55 text-sm",
        variant === "hero" ? "bg-gradient-to-br from-[#FAFAF7] via-white to-[#F4F6FA] shadow-sm" : "",
        variant === "hero" ? "px-5 py-5 sm:px-6 sm:py-6" : "p-4",
      )}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
      data-testid="proposal-customer-pricing"
    >
      {negotiated ? (
        <>
          {primaryIsPayable ? (
            <div data-testid="proposal-pricing-primary-payable">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#1A2744]">
                Final Amount Payable
              </p>
              <p className={cn("mt-2", payableHeroClass)} data-testid="proposal-final-amount-payable">
                {money.format(finalAmountPayable)}
              </p>
            </div>
          ) : primaryIsAgreed ? (
            <div data-testid="proposal-pricing-primary-agreed">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#1A2744]">
                Final Agreed Price
              </p>
              <p className={cn("mt-2", agreedHeroClass)}>{money.format(finalAgreedPrice)}</p>
            </div>
          ) : null}

          <div
            className={cn(
              "space-y-2",
              primaryIsPayable || primaryIsAgreed ? "mt-4 border-t border-[#1A2744]/10 pt-4" : "",
            )}
          >
            {primaryIsPayable ? (
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#1A2744]/70">
                  Final Agreed Price
                </span>
                <span className="text-base font-semibold tabular-nums text-[#1A2744]">
                  {money.format(finalAgreedPrice)}
                </span>
              </div>
            ) : null}
            {showGst ? (
              <div className="flex items-center justify-between gap-4">
                <span className={proposalLabelClass}>GST @ 9%</span>
                <span className="text-sm font-medium tabular-nums text-[#1A2744]/75">
                  {money.format(gstAmount)}
                </span>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-4">
              <span className={proposalLabelClass}>Proposal Total</span>
              <span className={proposalTotalClass}>{money.format(proposalTotal)}</span>
            </div>
            {displayDiscount ? (
              <div
                className="flex items-center justify-between gap-4"
                data-testid="proposal-pricing-discount"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#1A2744]/70">
                  {discountLabel}
                </span>
                <span className="text-sm font-semibold tabular-nums text-[#1A2744]">
                  −{money.format(resolvedDiscountAmount)}
                </span>
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <span className={proposalLabelClass}>Proposal Total</span>
            <span className={proposalTotalClass}>{money.format(proposalTotal)}</span>
          </div>
          {showGst ? (
            <div className="mt-2 flex items-center justify-between gap-4 border-t border-[#1A2744]/10 pt-2">
              <span className={proposalLabelClass}>GST @ 9%</span>
              <span className="font-medium tabular-nums text-[#1A2744]">{money.format(gstAmount)}</span>
            </div>
          ) : null}
          {showFinalAmountPayable && showGst ? (
            <div
              className="mt-3 flex items-center justify-between gap-4 border-t-2 border-[#1A2744]/12 pt-3"
              data-testid="proposal-final-amount-payable"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#1A2744]">
                Final Amount Payable
              </span>
              <span className="text-lg font-semibold tabular-nums text-[#1A2744]">
                {money.format(finalAmountPayable)}
              </span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function ProposalCustomerPricingPanelFromDisplay({
  pricing,
  variant = "default",
}: {
  pricing: ProposalCustomerPricingDisplay;
  variant?: "default" | "hero";
}) {
  return (
    <ProposalCustomerPricingPanel
      proposalTotal={pricing.proposalTotal}
      finalAgreedPrice={pricing.finalAgreedPrice}
      showFinalAgreedPrice={pricing.showFinalAgreedPrice}
      discountAmount={pricing.discountAmount}
      discountPercentage={pricing.discountPercentage}
      showDiscount={pricing.showDiscount}
      showDiscountPercentage={pricing.showDiscountPercentage}
      gstAmount={pricing.gstAmount}
      showGst={pricing.showGst}
      finalAmountPayable={pricing.finalAmountPayable}
      showFinalAmountPayable={pricing.showFinalAmountPayable}
      variant={variant}
    />
  );
}
